import { useState } from 'react';
import { useProject, useUpdateProject } from '../../hooks/useProjects';
import { Button, EmptyState, Input } from '../../ui';
import './AllowedOriginsSettings.css';

const MAX_ORIGINS = 20;

interface AllowedOriginsSettingsProps {
  projectId: string;
  userRole?: 'viewer' | 'member' | 'admin' | 'owner';
}

export function AllowedOriginsSettings({
  projectId,
  userRole = 'member',
}: AllowedOriginsSettingsProps) {
  const { data: project, isLoading, error } = useProject(projectId);
  const updateProject = useUpdateProject();

  const [input, setInput] = useState('');
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const canManage = userRole === 'admin' || userRole === 'owner';
  const origins = project?.allowed_origins ?? [];
  const atCapacity = origins.length >= MAX_ORIGINS;

  const showFlash = (kind: 'success' | 'error', text: string) => {
    setFlash({ kind, text });
    window.setTimeout(() => setFlash(null), 4000);
  };

  const submitUpdate = async (next: string[]) => {
    try {
      await updateProject.mutateAsync({ projectId, data: { allowed_origins: next } });
      return true;
    } catch (err) {
      showFlash('error', (err as Error).message);
      return false;
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    if (origins.includes(trimmed)) {
      showFlash('error', 'That origin is already in the list.');
      return;
    }
    const ok = await submitUpdate([...origins, trimmed]);
    if (ok) {
      setInput('');
      showFlash('success', 'Origin added.');
    }
  };

  const handleRemove = async (origin: string) => {
    const ok = await submitUpdate(origins.filter(o => o !== origin));
    if (ok) showFlash('success', 'Origin removed.');
  };

  if (isLoading) return <div className="origins-settings__loading">Loading origins…</div>;
  if (error || !project) {
    return (
      <div className="origins-settings__error">
        Failed to load project: {(error as Error | null)?.message ?? 'not found'}
      </div>
    );
  }

  return (
    <div className="origins-settings">
      <div className="origins-settings__header">
        <h2>Allowed Origins</h2>
        <p className="origins-settings__muted">
          Browser origins permitted to call this project's auth endpoints cross-site. Add{' '}
          <code>http://localhost:3000</code> to test a local app against this auth service.
        </p>
      </div>

      {flash && (
        <div className={`origins-settings__flash origins-settings__flash--${flash.kind}`}>
          {flash.text}
        </div>
      )}

      {canManage && (
        <form className="origins-settings__add" onSubmit={handleAdd}>
          <label htmlFor="origin-input" className="visually-hidden">
            Origin
          </label>
          <Input
            id="origin-input"
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="http://localhost:3000"
            autoComplete="off"
            disabled={atCapacity || updateProject.isPending}
            wrapperClassName="origins-settings__input"
          />
          <Button
            type="submit"
            disabled={!input.trim() || atCapacity || updateProject.isPending}
            loading={updateProject.isPending}
          >
            {updateProject.isPending ? 'Adding…' : 'Add origin'}
          </Button>
        </form>
      )}

      <p className="origins-settings__meta">
        {origins.length} of {MAX_ORIGINS} used
      </p>

      {origins.length === 0 ? (
        <EmptyState
          title="No allowed origins yet."
          description="Requests from browsers not listed here will be blocked by CORS."
        />
      ) : (
        <ul className="origins-settings__list">
          {origins.map(origin => (
            <li key={origin} className="origin-row">
              <code className="origin-row__value">{origin}</code>
              {canManage && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemove(origin)}
                  disabled={updateProject.isPending}
                  aria-label={`Remove ${origin}`}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
